import { useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarPlus, ChevronLeft, IdCard } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { ConfirmDeleteDialog } from 'components/ConfirmDeleteDialog';
import AppContext from 'contexts/AppContext';
import { apiErrorMessage } from 'utils/errorMessage';
import { useMyUnavailabilities, useDeleteUnavailability } from 'hooks/useUnavailabilities';
import { useMyTechnicianProfile } from 'hooks/useTechnicians';
import type { UnavailabilityPeriod } from 'api/unavailabilitiesApi';
import type { ReferentialTag } from 'api/techniciansApi';
import { AvailabilityBadge } from 'components/AvailabilityBadge';
import { availabilityHint, formatPeriod } from 'utils/unavailability';
import InfoRow from 'components/InfoRow';
import DeclareUnavailabilitySheet from './DeclareUnavailabilitySheet';
import UnavailabilityPeriodList from './UnavailabilityPeriodList';

/**
 * The technician's own screen, in the WebView and in a browser alike — the mobile is an Expo
 * shell around this very React app, so this is a React page and the binary does not move.
 *
 * It shows the derived availability, the declared periods, the button that opens the declaration
 * sheet, and the identity the back office keeps. Nothing is derived here: `state`, `days` and the
 * availability itself are computed by Postgres, so this screen compares no dates.
 *
 * No theme toggle — the mock-up carries one and the design review asked for it to go. There is no
 * theme switch anywhere in this application to remove, so it is simply not added.
 */

const CARD = 'rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900';
/** 44 px, the smallest target a thumb hits reliably — the mock-up draws 30. */
const TAP = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl';
const HINT = 'text-sm leading-relaxed text-zinc-500 dark:text-zinc-400';

/** An em dash and never an empty line: a blank value would read as "not applicable". */
const DASH = '—';

/** Referential values read as their names, comma-separated — the codes mean nothing to a phone. */
const tagNames = (tags: ReferentialTag[]): string =>
  tags.length === 0 ? DASH : tags.map((tag) => tag.name).join(', ');

const TechnicianProfile = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useContext(AppContext);
  const { data, isLoading, error } = useMyUnavailabilities();
  const identity = useMyTechnicianProfile();
  const remove = useDeleteUnavailability();

  const [declaring, setDeclaring] = useState(false);
  const [pending, setPending] = useState<UnavailabilityPeriod | null>(null);

  /**
   * gps-app opens this page in a native screen with its own title and back (`?nativeHeader=1`).
   * The web bar would be a second title, and its chevron would lead to the tickets *inside* that
   * screen. In a browser the bar stays: it is the only way back.
   */
  const nativeHeader = searchParams.get('nativeHeader') === '1';

  const periods = data?.periods ?? [];
  const availability = data?.availability ?? 'available';
  /**
   * Only a successful read may make a claim. Neither the badge nor the empty state falls back to
   * "available" when the request failed — both would read as "you are free", which is the one
   * thing this screen must never say without knowing.
   */
  const loaded = !isLoading && !error;

  const confirmDelete = () => {
    if (!pending) return;
    remove.mutate(pending.id, { onSuccess: () => setPending(null) });
  };

  // Under the native header the web owns the first pixel, hence `pt-4`.
  return (
    <div className={cn('flex flex-col gap-3 pb-8', nativeHeader && 'pt-4')}>
      {!nativeHeader && (
        <div className="flex items-center gap-1 border-b border-zinc-200 pb-3 dark:border-zinc-800">
          <button
            type="button"
            onClick={() => navigate('/issues')}
            aria-label={t('Back')}
            className={cn(TAP, '-ml-2 text-zinc-900 dark:text-zinc-100')}
          >
            <ChevronLeft className="h-6 w-6" aria-hidden="true" />
          </button>
          <div className="min-w-0">
            <p className="text-base font-semibold text-zinc-900 dark:text-zinc-50">{t('My profile')}</p>
            <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">{currentUser?.name}</p>
          </div>
        </div>
      )}

      {/* First: who the backoffice says he is, before anything he can act on. Read-only
          without exception (D11) — the backoffice owns these fields, he only reads them. */}
      <section className={cn(CARD, 'overflow-hidden')}>
        <div className="flex items-center gap-2 px-4 pb-3 pt-4">
          <IdCard className="h-4 w-4 text-zinc-400" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{t('My information')}</h2>
        </div>

        {identity.isLoading ? (
          <div className="space-y-2 border-t border-zinc-200 p-4 dark:border-zinc-800">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : identity.error || !identity.data ? (
          // Five empty rows would read as "nothing on file", which is not what is known.
          <p className="border-t border-zinc-200 px-4 py-4 text-sm leading-relaxed text-red-700 dark:border-zinc-800 dark:text-red-300">
            {t('Your information could not be loaded.')}
          </p>
        ) : (
          <>
            <InfoRow truncate label={t('Phone')} value={identity.data.profile.phone || DASH} />
            <InfoRow truncate label={t('Email')} value={identity.data.email || DASH} />
            <InfoRow truncate label={t('Country')} value={identity.data.profile.countryName || DASH} />
            <InfoRow
              truncate
              label={t('Department', { count: identity.data.geoAreas.length })}
              value={tagNames(identity.data.geoAreas)}
            />
            <InfoRow truncate label={t('Specialties')} value={tagNames(identity.data.specialties)} />
            <p className="border-t border-zinc-200 px-4 py-3 text-xs leading-relaxed text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
              {t('These fields are kept by the back office. Ask it for a correction.')}
            </p>
          </>
        )}
      </section>

      {/* A failed read has no banner of its own: the two cards below say what they cannot say,
          each in its own place, and the concrete reason sits where the list would have been. */}
      <section className={cn(CARD, 'p-4')}>
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{t('Availability')}</h2>
          {/* A failed read claims nothing: no badge at all rather than "Disponible", which is what
              a reader would take away from a screen that could not load a single period. */}
          {isLoading && <Skeleton className="h-5 w-24 rounded-full" />}
          {loaded && <AvailabilityBadge state={availability} />}
        </div>
        {/* Three siblings and not one paragraph with a `Skeleton` inside it: `Skeleton` renders a
            `div`, which is not allowed inside a `<p>` — the browser closed the paragraph early and
            React logged a `validateDOMNesting` warning on every load. */}
        {isLoading && <Skeleton className="mt-3 h-9 w-full" />}
        {loaded && <p className={cn('mt-3', HINT)}>{availabilityHint(t, availability, periods)}</p>}
        {/* The declaration below stays offered even then: it is what the technician came for,
            and the server is what checks the overlap. */}
        {error && (
          <p className={cn('mt-3', HINT)}>
            {t('Your declared periods could not be loaded, so this screen cannot say whether you are available. You can still declare a period.')}
          </p>
        )}
        {/* The shared Button, and not the mock-up's hard-coded `--primary`: that token is the one
            its dark palette forgets to redefine, which is why the design review found this very
            button invisible in dark. This variant carries both sets. */}
        <Button className="mt-4 h-12 w-full text-sm" onClick={() => setDeclaring(true)}>
          <CalendarPlus className="mr-1.5 h-4 w-4" aria-hidden="true" />
          {t('Declare an unavailability')}
        </Button>
      </section>

      <section className={cn(CARD, 'overflow-hidden')}>
        <div className="flex items-center gap-2 px-4 pb-2 pt-4">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
            {t('My unavailability periods')}
          </h2>
          <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-md bg-zinc-100 px-1.5 text-[11px] font-semibold text-zinc-500 dark:bg-zinc-800 dark:text-zinc-400">
            {loaded ? periods.length : '–'}
          </span>
        </div>

        <UnavailabilityPeriodList
          periods={periods}
          isLoading={isLoading}
          error={error}
          onDelete={setPending}
        />
      </section>
      <DeclareUnavailabilitySheet open={declaring} onClose={() => setDeclaring(false)} />

      {pending && (
        <ConfirmDeleteDialog
          open
          title={t('Delete this unavailability period?')}
          description={t(
            'You will be proposed again for the interventions covering these dates. You can declare the period again afterwards.'
          )}
          details={[
            { label: t('Period'), value: formatPeriod(pending.startDate, pending.endDate), emphasis: true },
          ]}
          loading={remove.isPending}
          error={remove.error ? apiErrorMessage(t, remove.error) : null}
          onClose={() => setPending(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

export default TechnicianProfile;
